import AppContext from 'App/AppContext';
import { IPStack, IPText } from 'baseComponents';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { globalStackTokensGapSmall } from 'globalStyles';
import { FormArchivePreview } from 'models/tasks/formVersion';
import { TaskTypes } from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetFormArchivePreview } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';

interface IFormArchiveModalProps {
  taskType: TaskType;
  isOpen: boolean;
  onDismiss: () => void;
  onArchive: () => Promise<void>;
}

const FormArchiveModal = (props: IFormArchiveModalProps) => {
  const { t } = useTranslation(['forms']);
  const appContext = useContext(AppContext);
  const [preview, setPreview] = useState<FormArchivePreview | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen || !props.taskType.taskTypeFamilyId) {
      return;
    }

    const loadPreview = async () => {
      try {
        setIsLoading(true);
        setPreview(undefined);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetFormArchivePreview(props.taskType.taskTypeFamilyId as number, accessToken);
        setPreview(result);
      } catch (error) {
        appContext.setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreview();
  }, [appContext, props.isOpen, props.taskType.taskTypeFamilyId]);

  const blockingConcreteTaskCount = preview?.blockingConcreteTaskCount ?? 0;
  const inaccessibleProducerCount = preview?.inaccessibleProducerCount ?? 0;
  const isBlocked = blockingConcreteTaskCount > 0 || inaccessibleProducerCount > 0;
  // The archive detaches all bound producers, but only templates are listed: recurring series masters
  // and event headers can run into the hundreds, and a list that long hides the question.
  const templates = (preview?.producers ?? []).filter(
    (producer) => producer.taskType === TaskTypes.Template || producer.taskType === TaskTypes.EventTemplate,
  );
  // No details are drawn while the preview loads; Archive stays disabled until it is in (see
  // disableYes), which avoids resizing the dialog twice.
  const hasDetails = !isLoading && (isBlocked || templates.length > 0);

  const archive = async () => {
    try {
      setIsSaving(true);
      await props.onArchive();
      props.onDismiss();
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDetails = (): JSX.Element => (
    <IPStack tokens={globalStackTokensGapSmall}>
      {blockingConcreteTaskCount > 0 && (
        <IPStack.Item>
          <IPText>{t('forms:Versioning.ArchivePreview.ConcreteBlocker', { count: blockingConcreteTaskCount })}</IPText>
        </IPStack.Item>
      )}
      {inaccessibleProducerCount > 0 && (
        <IPStack.Item>
          <IPText>
            {t('forms:Versioning.ArchivePreview.InaccessibleProducer', { count: inaccessibleProducerCount })}
          </IPText>
        </IPStack.Item>
      )}
      {templates.length > 0 && !isBlocked && (
        <>
          <IPStack.Item>
            <IPText>{t('forms:Versioning.ArchivePreview.Templates')}</IPText>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { maxHeight: '40vh', overflowY: 'auto' } }}>
            {templates.map((template) => (
              <IPText key={template.taskId} block>
                {template.name}
              </IPText>
            ))}
          </IPStack.Item>
        </>
      )}
    </IPStack>
  );

  return (
    <DialogYesNo
      hidden={!props.isOpen}
      title={t('forms:Versioning.ArchiveTitle')}
      subText={t('forms:Versioning.ArchiveText', { name: props.taskType.name })}
      yesText={t('forms:Versioning.Archive')}
      disableYes={isLoading || isSaving || !preview || isBlocked}
      optionalJSX={hasDetails ? renderDetails() : undefined}
      optionalJSXGrow={true}
      dialogStyles={hasDetails ? { main: { maxWidth: 560 } } : undefined}
      onYes={() => void archive()}
      onNo={props.onDismiss}
    />
  );
};

export default FormArchiveModal;
